import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { creatorService } from "../../services/creatorService";
import PatreonCreatorCard from "../../components/PatreonCreatorCard";

const PublicDiscoverPage: React.FC = () => {
  const [query, setQuery] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [creators, setCreators] = React.useState<any[]>([]);
  const [total, setTotal] = React.useState(0);
  const limit = 12;
  const [offset, setOffset] = React.useState(0);
  const [category, setCategory] = React.useState<string>("");
  const [minPatrons, setMinPatrons] = React.useState<string>("");
  const [maxPatrons, setMaxPatrons] = React.useState<string>("");
  const [sort, setSort] = React.useState<"popular" | "newest" | "az">("popular");
  const [aggregations, setAggregations] = React.useState<{ patronCount?: { key: string; count: number }[]; categories?: { key: string; count: number }[] } | undefined>();
  const navigate = useNavigate();

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await creatorService.searchCreators({
        query,
        limit,
        offset,
        category: category || undefined,
        minPatrons: minPatrons ? Number(minPatrons) : undefined,
        maxPatrons: maxPatrons ? Number(maxPatrons) : undefined,
        sort,
      });
      setCreators(res.creators);
      setTotal(res.pagination.total);
      setAggregations(res.aggregations);
    } catch (e: any) {
      setError(e?.message || "Failed to load creators");
    } finally {
      setLoading(false);
    }
  }, [query, limit, offset, category, minPatrons, maxPatrons, sort]);

  React.useEffect(() => {
    const t = setTimeout(() => load(), 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--patreon-cream)' }}>
      <Helmet>
        <title>Discover Creators | Patreon</title>
        <meta name="description" content="Find creators across categories to support on Patreon." />
        <link rel="canonical" href={window.location.origin + "/discover"} />
        <meta property="og:title" content="Discover Creators | Patreon" />
        <meta property="og:description" content="Find artists, writers, and communities to support." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Discover Creators | Patreon" />
        <meta name="twitter:description" content="Find artists, writers, and communities to support." />
      </Helmet>
      <div className="max-w-7xl mx-auto px-6 py-16">
        <header className="mb-8">
          <h1 className="text-4xl font-bold" style={{ color: 'var(--patreon-black)' }}>Discover creators</h1>
          <p className="mt-3 text-lg" style={{ color: 'var(--patreon-gray)' }}>Find artists, writers, and communities to support.</p>
          <div className="mt-4 flex gap-2">
            <input
              value={query}
              onChange={(e) => { setOffset(0); setQuery(e.target.value); }}
              placeholder="Search creators"
              className="flex-1 px-4 py-2 rounded-lg"
              style={{ background: 'var(--patreon-card-bg)', border: '1px solid var(--patreon-card-border)', color: 'var(--patreon-black)' }}
            />
            <button onClick={() => { setOffset(0); load(); }} className="px-4 py-2 rounded-lg text-white" style={{ background: 'var(--patreon-btn-primary-bg)' }}>Search</button>
          </div>
          {/* Filters */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-5 gap-3">
            <select
              value={category}
              onChange={(e) => { setOffset(0); setCategory(e.target.value); }}
              className="px-3 py-2 rounded-lg"
              style={{ background: 'var(--patreon-card-bg)', border: '1px solid var(--patreon-card-border)', color: 'var(--patreon-black)' }}
            >
              <option value="">All categories</option>
              <option value="Art">Art</option>
              <option value="Writing">Writing</option>
              <option value="Music">Music</option>
              <option value="Podcasts">Podcasts</option>
              <option value="Gaming">Gaming</option>
              <option value="Education">Education</option>
            </select>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              value={minPatrons}
              onChange={(e) => { setOffset(0); setMinPatrons(e.target.value); }}
              placeholder="Min patrons"
              className="px-3 py-2 rounded-lg"
              style={{ background: 'var(--patreon-card-bg)', border: '1px solid var(--patreon-card-border)', color: 'var(--patreon-black)' }}
            />
            <input
              type="number"
              inputMode="numeric"
              min={0}
              value={maxPatrons}
              onChange={(e) => { setOffset(0); setMaxPatrons(e.target.value); }}
              placeholder="Max patrons"
              className="px-3 py-2 rounded-lg"
              style={{ background: 'var(--patreon-card-bg)', border: '1px solid var(--patreon-card-border)', color: 'var(--patreon-black)' }}
            />
            <select
              value={sort}
              onChange={(e) => { setOffset(0); setSort(e.target.value as any); }}
              className="px-3 py-2 rounded-lg"
              style={{ background: 'var(--patreon-card-bg)', border: '1px solid var(--patreon-card-border)', color: 'var(--patreon-black)' }}
            >
              <option value="popular">Most popular</option>
              <option value="newest">Newest</option>
              <option value="az">A–Z</option>
            </select>
            <button
              onClick={() => { setOffset(0); load(); }}
              className="px-4 py-2 rounded-lg text-white"
              style={{ background: 'var(--patreon-btn-primary-bg)', boxShadow: 'var(--patreon-btn-primary-shadow)' }}
            >
              Apply filters
            </button>
          </div>
          {aggregations?.categories && aggregations.categories.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {aggregations.categories.map((b) => (
                <button
                  key={b.key}
                  onClick={() => { setCategory(b.key); setOffset(0); }}
                  className={`px-3 py-1 rounded-full text-sm ${category === b.key ? 'text-white' : ''}`}
                  style={{ background: category === b.key ? 'var(--patreon-btn-primary-bg)' : 'var(--patreon-card-bg)', border: '1px solid var(--patreon-card-border)', color: category === b.key ? 'white' : 'var(--patreon-black)' }}
                >
                  {b.key} ({b.count})
                </button>
              ))}
            </div>
          )}
          {aggregations?.patronCount && (
            <div className="mt-3 text-sm" style={{ color: 'var(--patreon-gray)' }}>
              Popular ranges: {aggregations.patronCount.map(b => `${b.key} (${b.count})`).join(' • ')}
            </div>
          )}
        </header>

        {error && <div className="mb-4 text-sm" style={{ color: 'var(--patreon-error)' }}>{error}</div>}

        {loading ? (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse p-4 rounded-lg" style={{ background: 'var(--patreon-card-bg)', border: '1px solid var(--patreon-card-border)' }}>
                  <div className="h-24 w-24 rounded-full mb-4" style={{ background: 'var(--patreon-gray-lightest)' }} />
                  <div className="h-4 w-1/2 mb-2" style={{ background: 'var(--patreon-gray-lightest)' }} />
                  <div className="h-4 w-3/4" style={{ background: 'var(--patreon-gray-lightest)' }} />
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {creators.map((c, idx) => (
                <div key={c.id} onClick={() => navigate(`/creator/${c.customUrl || c.id}`)} className="cursor-pointer">
                  <PatreonCreatorCard
                    creator={{
                      name: c.displayName || c.user?.username || 'Creator',
                      category: category || 'Creator',
                      description: c.description || '',
                      avatar: c.user?.avatar,
                      supporters: c._count?.memberships || 0,
                      gradient: idx % 2 === 0 ? 'from-patreon-orange to-patreon-pink' : 'from-patreon-blue to-patreon-purple',
                    }}
                    onCreatorClick={() => navigate(`/creator/${c.customUrl || c.id}`)}
                  />
                </div>
              ))}
            </div>
            {!loading && creators.length === 0 && (
              <div className="mt-10 text-center" style={{ color: 'var(--patreon-gray)' }}>
                No results. Try adjusting filters or search terms.
              </div>
            )}

            <div className="mt-8 flex items-center justify-between">
              <button
                className="px-4 py-2 rounded-lg"
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                style={{ background: 'var(--patreon-gray-lightest)', color: 'var(--patreon-black)', opacity: offset === 0 ? 0.5 : 1 }}
              >
                Previous
              </button>
              <div style={{ color: 'var(--patreon-gray)' }}>
                Page {Math.floor(offset / limit) + 1} of {Math.max(1, Math.ceil(total / limit))}
              </div>
              <button
                className="px-4 py-2 rounded-lg text-white"
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= total}
                style={{ background: 'var(--patreon-btn-primary-bg)', opacity: offset + limit >= total ? 0.5 : 1 }}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicDiscoverPage;


